import {
  removeProjectProofRecord,
  upsertProjectProofRecord,
} from '@/services/project-api'
import {
  buildAgenticProductionCanaryModel,
  findAgenticProductionCanaryEvidence,
} from './agentic-production-canary'
import type {
  AgenticProductionCanaryEvidence,
  AgenticProductionCanaryFact,
  AgenticProductionCanaryModel,
} from './agentic-production-canary'

export type StudioProofRecordKind =
  | 'production-studio-readback'
  | 'native-office-parity-proof'
  | 'browser-render-proof'
  | 'design-system-proof'
  | 'cleanup-proof'

export type StudioProofRecordStatus = 'pass' | 'review' | 'blocked'

export interface StudioProofRecordFact {
  label: string
  value: string
}

export interface StudioProofRecord {
  id: string
  projectId?: string
  kind: StudioProofRecordKind
  status: StudioProofRecordStatus
  title: string
  summary: string
  source: string
  createdAt: string
  facts: StudioProofRecordFact[]
  missing: string[]
  failureReasons: string[]
  evidence?: Record<string, unknown>
}

export const STUDIO_PROOF_RECORDS_STORAGE_PREFIX = 'cogo-studio:proof-records:v1:'

export function proofRecordsStorageKey(projectId: string | null | undefined): string {
  return `${STUDIO_PROOF_RECORDS_STORAGE_PREFIX}${projectId || 'unscoped'}`
}

export function loadStoredStudioProofRecords(projectId: string | null | undefined): StudioProofRecord[] {
  if (typeof window === 'undefined') return []
  const raw = window.localStorage.getItem(proofRecordsStorageKey(projectId))
  if (!raw) return []

  try {
    const parsed = JSON.parse(raw)
    return normalizeProofRecordArray(parsed)
  } catch {
    window.localStorage.removeItem(proofRecordsStorageKey(projectId))
    return []
  }
}

export function saveStoredStudioProofRecord(
  projectId: string | null | undefined,
  record: StudioProofRecord,
): StudioProofRecord[] {
  const next = mergeStudioProofRecords([...loadStoredStudioProofRecords(projectId), record])
  if (typeof window !== 'undefined') {
    window.localStorage.setItem(proofRecordsStorageKey(projectId), JSON.stringify(next))
  }
  return next
}

export function removeStoredStudioProofRecord(
  projectId: string | null | undefined,
  recordId: string,
): StudioProofRecord[] {
  const next = loadStoredStudioProofRecords(projectId).filter((record) => record.id !== recordId)
  if (typeof window !== 'undefined') {
    if (next.length > 0) {
      window.localStorage.setItem(proofRecordsStorageKey(projectId), JSON.stringify(next))
    } else {
      window.localStorage.removeItem(proofRecordsStorageKey(projectId))
    }
  }
  return next
}

export function buildStudioProofRecordsFromProject(
  projectId: string | null | undefined,
  ...sources: unknown[]
): StudioProofRecord[] {
  const storedRecords = loadStoredStudioProofRecords(projectId)
  const projectRecords = sources.flatMap(readProjectProofRecords)
  const embeddedRecords = sources.flatMap((source) => {
    const evidence = findAgenticProductionCanaryEvidence(source)
    return evidence
      ? [
          buildProductionCanaryProofRecord({
            projectId,
            evidence,
            model: buildAgenticProductionCanaryModel(evidence),
          }),
        ]
      : []
  })
  const officeResidualRecords = sources.flatMap((source) =>
    findOfficeVisualResidualProofEvidence(source).map((evidence) =>
      buildOfficeVisualResidualProofRecord({
        projectId,
        evidence,
      }),
    ),
  )

  return mergeStudioProofRecords([...storedRecords, ...projectRecords, ...embeddedRecords, ...officeResidualRecords])
}

export function readProjectProofRecords(project: unknown): StudioProofRecord[] {
  const metadata = asRecord(asRecord(project).metadata)
  return normalizeProofRecordArray(metadata.proofRecords ?? metadata.libraryProofRecords)
}

export function findLatestProductionReadbackProofRecord(
  records: StudioProofRecord[],
): StudioProofRecord | null {
  return records
    .filter((record) => record.kind === 'production-studio-readback')
    .sort((a, b) => b.createdAt.localeCompare(a.createdAt))[0] ?? null
}

export function getProductionReadbackEvidenceFromProofRecord(
  record: StudioProofRecord | null | undefined,
): AgenticProductionCanaryEvidence | null {
  if (!record || record.kind !== 'production-studio-readback') return null
  const evidence = asRecord(record.evidence)
  return evidence.source ? evidence as unknown as AgenticProductionCanaryEvidence : null
}

export async function saveProjectBackedStudioProofRecord(
  projectId: string | null | undefined,
  record: StudioProofRecord,
): Promise<StudioProofRecord[]> {
  const localRecords = saveStoredStudioProofRecord(projectId, record)
  if (projectId) {
    await upsertProjectProofRecord(projectId, record as unknown as Record<string, unknown>)
  }
  return localRecords
}

export async function removeProjectBackedStudioProofRecord(
  projectId: string | null | undefined,
  recordId: string,
): Promise<StudioProofRecord[]> {
  const localRecords = removeStoredStudioProofRecord(projectId, recordId)
  if (projectId) {
    await removeProjectProofRecord(projectId, recordId)
  }
  return localRecords
}

export function mergeStudioProofRecords(records: StudioProofRecord[]): StudioProofRecord[] {
  const byId = new Map<string, StudioProofRecord>()
  for (const record of records) {
    byId.set(record.id, record)
  }
  return Array.from(byId.values()).sort((a, b) => b.createdAt.localeCompare(a.createdAt))
}

export function buildProductionCanaryProofRecord({
  projectId,
  evidence,
  model,
  createdAt,
}: {
  projectId: string | null | undefined
  evidence: AgenticProductionCanaryEvidence
  model: AgenticProductionCanaryModel
  createdAt?: string
}): StudioProofRecord {
  const facts = model.facts
    .filter((fact) => !fact.missing && fact.value !== 'missing')
    .map(toProofRecordFact)
  const tempProjectId = factValue(model.facts, 'Temporary project id')
  const payloadHash = factValue(model.facts, 'Payload hash')
  const cleanupProof = factValue(model.facts, 'Cleanup proof')

  return {
    id: stableRecordId('production-studio-readback', tempProjectId || payloadHash || projectId || 'pending'),
    ...(projectId ? { projectId } : {}),
    kind: 'production-studio-readback',
    status: statusFromCanaryModel(model),
    title: 'Production launch check',
    summary: model.summary,
    source: 'production-studio-readback',
    createdAt: createdAt || firstString(evidence.generatedAt, evidence.createdAt, evidence.timestamp) || new Date().toISOString(),
    facts: uniqueFacts([
      ...facts,
      ...(tempProjectId ? [{ label: 'Project', value: tempProjectId }] : []),
      ...(payloadHash ? [{ label: 'Hash', value: payloadHash }] : []),
      ...(cleanupProof ? [{ label: 'Cleanup', value: cleanupProof }] : []),
    ]),
    missing: model.missing,
    failureReasons: model.failureReasons,
    evidence,
  }
}

interface OfficeVisualResidualEvidence {
  sourceCommit?: string
  createdAt?: string
  target: Record<string, unknown>
  proof: Record<string, unknown>
}

export function findOfficeVisualResidualProofEvidence(...sources: unknown[]): OfficeVisualResidualEvidence[] {
  const results: OfficeVisualResidualEvidence[] = []
  const seen = new Set<string>()
  const visited = new WeakSet<object>()

  for (const source of sources) {
    scanOfficeVisualResidualEvidence(source, results, seen, visited, 0)
  }
  return results
}

export function buildOfficeVisualResidualProofRecord({
  projectId,
  evidence,
}: {
  projectId: string | null | undefined
  evidence: OfficeVisualResidualEvidence
}): StudioProofRecord {
  const target = evidence.target
  const proof = evidence.proof
  const format = firstString(target.format, proof.format) || 'office'
  const proofStatus = firstString(proof.status) || 'unknown'
  const productionReady = target.productionReady === true
  const parityPass = asRecord(target.productionParityProof).pass === true
  const diagnosticOnly = proof.diagnosticOnly === true
  const productionReadinessEvidence = proof.productionReadinessEvidence === true
  const diagnostics = asRecord(proof.diagnostics)
  const tableProof = asRecord(diagnostics.tableResidualProfileProof)
  const denseFlowProof = asRecord(diagnostics.denseTableFlowProof)
  const imageProof = asRecord(diagnostics.imageResidualProof)
  const cropProof = asRecord(diagnostics.paragraphLineBoxCropProof)
  const topIssueClass = firstObjectKey(asRecord(asRecord(proof.totals).byIssueClass))
  const topTableProfile = firstObjectKey(asRecord(tableProof.byStructuralProfile))
  const failureReasons = [
    ...(proofStatus === 'failed' ? ['Office visual residual attribution proof failed.'] : []),
    ...(productionReadinessEvidence ? ['Diagnostic residual proof is incorrectly marked as production readiness evidence.'] : []),
  ]

  return {
    id: stableRecordId('native-office-parity-proof', `${format}:${firstString(proof.outputPath, proof.path) || evidence.sourceCommit || 'pending'}`),
    ...(projectId ? { projectId } : {}),
    kind: 'native-office-parity-proof',
    status: failureReasons.length > 0 ? 'blocked' : productionReady && parityPass ? 'pass' : 'review',
    title: `${format.toUpperCase()} native Office residual proof`,
    summary: productionReady && parityPass
      ? `${format.toUpperCase()} native Office target has production readiness while residual attribution remains visible as diagnostic evidence.`
      : `${format.toUpperCase()} native Office target still needs review; residual attribution separates table, image, and paragraph causes.`,
    source: 'office-visual-residual-attribution',
    createdAt: evidence.createdAt || firstString(proof.createdAt) || new Date().toISOString(),
    facts: uniqueFacts([
      { label: 'Format', value: format },
      { label: 'Check status', value: proofStatus },
      { label: 'Diagnostic only', value: String(diagnosticOnly) },
      { label: 'ProductionReady', value: String(productionReady) },
      ...(topIssueClass ? [{ label: 'Top issue', value: topIssueClass }] : []),
      ...(topTableProfile ? [{ label: 'Top table profile', value: topTableProfile }] : []),
      { label: 'Dense flow pages', value: stringFromUnknownArray(denseFlowProof.highRiskPages) || 'none' },
      { label: 'Image residual samples', value: String(asArray(imageProof.samples).length) },
      { label: 'Paragraph crop severe', value: String(firstStringOrNumber(cropProof.severeSampleCount) || 0) },
    ]),
    missing: [
      ...(!diagnosticOnly ? ['diagnosticOnly=true marker'] : []),
      ...(Object.keys(asRecord(proof.totals)).length === 0 ? ['residual totals'] : []),
      ...(Object.keys(diagnostics).length === 0 ? ['P1-P4 residual diagnostics'] : []),
    ],
    failureReasons,
    evidence: {
      source: 'office-visual-residual-attribution',
      sourceCommit: evidence.sourceCommit,
      format,
      targetProductionReady: productionReady,
      productionParityPass: parityPass,
      officeVisualResidualAttributionProof: proof,
    },
  }
}

function normalizeProofRecordArray(value: unknown): StudioProofRecord[] {
  if (!Array.isArray(value)) return []
  return value.map(normalizeProofRecord).filter((record): record is StudioProofRecord => Boolean(record))
}

function normalizeProofRecord(value: unknown): StudioProofRecord | null {
  const record = asRecord(value)
  const id = firstString(record.id)
  const kind = normalizeKind(record.kind)
  const status = normalizeStatus(record.status)
  const title = firstString(record.title)
  const source = firstString(record.source)
  const createdAt = firstString(record.createdAt)
  if (!id || !kind || !status || !title || !source || !createdAt) return null

  return {
    id,
    projectId: firstString(record.projectId),
    kind,
    status,
    title,
    summary: firstString(record.summary) || title,
    source,
    createdAt,
    facts: normalizeFactArray(record.facts),
    missing: stringArray(record.missing),
    failureReasons: stringArray(record.failureReasons),
    evidence: asRecord(record.evidence),
  }
}

function normalizeKind(value: unknown): StudioProofRecordKind | null {
  return value === 'production-studio-readback'
    || value === 'native-office-parity-proof'
    || value === 'browser-render-proof'
    || value === 'design-system-proof'
    || value === 'cleanup-proof'
    ? value
    : null
}

function normalizeStatus(value: unknown): StudioProofRecordStatus | null {
  return value === 'pass' || value === 'review' || value === 'blocked' ? value : null
}

function normalizeFactArray(value: unknown): StudioProofRecordFact[] {
  if (!Array.isArray(value)) return []
  return value
    .map((item) => {
      const record = asRecord(item)
      const label = firstString(record.label)
      const factValue = firstString(record.value)
      return label && factValue ? { label, value: factValue } : null
    })
    .filter((item): item is StudioProofRecordFact => Boolean(item))
}

function statusFromCanaryModel(model: AgenticProductionCanaryModel): StudioProofRecordStatus {
  if (model.status === 'production_canary_passed') return 'pass'
  if (model.status === 'failed_closed' || model.status === 'production_env_missing') return 'blocked'
  return 'review'
}

function toProofRecordFact(fact: AgenticProductionCanaryFact): StudioProofRecordFact {
  return { label: fact.label, value: fact.value }
}

function factValue(facts: AgenticProductionCanaryFact[], label: string): string | undefined {
  return facts.find((fact) => fact.label === label && !fact.missing && fact.value !== 'missing')?.value
}

function uniqueFacts(facts: StudioProofRecordFact[]): StudioProofRecordFact[] {
  const byLabel = new Map<string, StudioProofRecordFact>()
  for (const fact of facts) {
    byLabel.set(fact.label, fact)
  }
  return Array.from(byLabel.values())
}

function stableRecordId(kind: StudioProofRecordKind, basis: string): string {
  return `${kind}:${basis.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'pending'}`
}

function scanOfficeVisualResidualEvidence(
  value: unknown,
  results: OfficeVisualResidualEvidence[],
  seen: Set<string>,
  visited: WeakSet<object>,
  depth: number,
): void {
  if (depth > 6 || value === null || typeof value !== 'object') return
  if (visited.has(value)) return
  visited.add(value)

  const record = asRecord(value)
  const sourceCommit = firstString(record.sourceCommit, record.commit, record.gitCommit)
  const createdAt = firstString(record.createdAt, record.generatedAt, record.timestamp)
  const directProof = asRecord(record.officeVisualResidualAttributionProof)
  if (Object.keys(directProof).length > 0) {
    pushOfficeVisualResidualEvidence(results, seen, {
      sourceCommit,
      createdAt,
      target: record,
      proof: directProof,
    })
  }

  const targets = asArray(record.targets)
  for (const targetValue of targets) {
    const target = asRecord(targetValue)
    const proof = asRecord(target.officeVisualResidualAttributionProof)
    if (Object.keys(proof).length === 0) continue
    pushOfficeVisualResidualEvidence(results, seen, {
      sourceCommit,
      createdAt,
      target,
      proof,
    })
  }

  for (const nested of Object.values(record)) {
    if (results.length >= 12) return
    if (nested === record.targets || nested === record.officeVisualResidualAttributionProof) continue
    if (nested && typeof nested === 'object') {
      scanOfficeVisualResidualEvidence(nested, results, seen, visited, depth + 1)
    }
  }
}

function pushOfficeVisualResidualEvidence(
  results: OfficeVisualResidualEvidence[],
  seen: Set<string>,
  evidence: OfficeVisualResidualEvidence,
): void {
  const key = [
    firstString(evidence.target.format, evidence.proof.format) || 'office',
    firstString(evidence.proof.outputPath, evidence.proof.path) || evidence.sourceCommit || String(results.length),
  ].join(':')
  if (seen.has(key)) return
  seen.add(key)
  results.push(evidence)
}

function stringArray(value: unknown): string[] {
  if (typeof value === 'string' && value.trim()) return [value.trim()]
  if (!Array.isArray(value)) return []
  return value.filter((item): item is string => typeof item === 'string' && item.trim().length > 0)
}

function asArray(value: unknown): unknown[] {
  return Array.isArray(value) ? value : []
}

function asRecord(value: unknown): Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {}
}

function firstStringOrNumber(...values: unknown[]): string | undefined {
  for (const value of values) {
    if (typeof value === 'string' && value.trim()) return value.trim()
    if (typeof value === 'number' && Number.isFinite(value)) return String(value)
  }
  return undefined
}

function firstString(...values: unknown[]): string | undefined {
  for (const value of values) {
    if (typeof value === 'string' && value.trim()) return value.trim()
  }
  return undefined
}

function firstObjectKey(record: Record<string, unknown>): string | undefined {
  return Object.keys(record)[0]
}

function stringFromUnknownArray(value: unknown): string {
  return asArray(value)
    .map((entry) => firstStringOrNumber(entry))
    .filter((entry): entry is string => Boolean(entry))
    .join(', ')
}
